/**
 * visitSearch — THE patient-search rule for the visits page (D44 / S19), and
 * its ratified copy.
 *
 * Pure module: no React, no API, no DOM. The search runs over the page's
 * ALREADY-LOADED list — it never fetches, so it can never turn typing into a
 * request storm — and it adds no visibility: it narrows the rows the viewer
 * could already see, inside whatever provider / assignee / department / status
 * filters are set.
 *
 * One matcher, used by the admin "Manage Visits" module and the doctor's own
 * visits view (the same page component serves both), so the two can never
 * disagree about what "matches" means.
 */

/** Everything but the digits — `+91 98765 43210` and `098765…` compare equal. */
export const digitsOnly = (v) => String(v ?? '').replace(/\D+/g, '');

/** The name as the row prints it: `patient_name`, else first + last. */
export function visitPatientName(visit) {
  const full = String(visit?.patient_name || '').trim();
  if (full) return full;
  return `${visit?.first_name || ''} ${visit?.last_name || ''}`.trim();
}

/** Collapse case and runs of whitespace, so "  Ram   Kumar " ≡ "ram kumar". */
const loose = (v) => String(v ?? '').toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * Does this visit match what the doctor typed?
 *
 * Name: case-insensitive substring, whitespace-insensitive, partial allowed.
 * Mobile: digits-only substring, so `98765`, `+91 98765 43210` and
 * `098765 43210` all hit the same row.
 *
 * An empty query matches EVERYTHING (the caller then renders the list
 * untouched, with no count line and no empty state).
 */
export function visitMatchesSearch(visit, query) {
  const q = loose(query);
  if (!q) return true;
  if (loose(visitPatientName(visit)).includes(q)) return true;
  const qDigits = digitsOnly(query);
  if (qDigits && digitsOnly(visit?.patient_mobile).includes(qDigits)) return true;
  return false;
}

/** The rows that match, in the order they were given. */
export function filterVisitsBySearch(visits, query) {
  if (!loose(query)) return visits || [];
  return (visits || []).filter((v) => visitMatchesSearch(v, query));
}

// ── Copy (PO-ratified, verbatim) ─────────────────────────────────────
export const VISIT_SEARCH_COPY = {
  placeholder: 'Search by patient name or mobile',
  aria: 'Search visits by patient name or mobile number',
  clearAria: 'Clear the visit search',
  /**
   * Nothing matched. When other filters are also on, the sentence says so —
   * the doctor is never left thinking a visit is gone when a date or status
   * filter is what hid it.
   */
  empty: (query, otherFiltersActive) => (otherFiltersActive
    ? `No visit matches "${query}" in the current filters.`
    : `No visit matches "${query}".`),
  /** Same shape as the templates list's count line. Never shown when empty. */
  count: (shown, total) => `Showing ${shown} of ${total} visit${total === 1 ? '' : 's'}`,
};

/** Debounce for the box: long enough to skip keystrokes, short enough to feel live. */
export const VISIT_SEARCH_DEBOUNCE_MS = 150;
